import React from 'react';
import { plural } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';

import Rebook from '@rover/icons/svg/rebook.svg';
import Trophy from '@rover/icons/svg/trophy.svg';
import { Flex } from '@rover/kibble/core';
import { getEngagementSignalContent } from '@rover/react-lib/src/pages/search/SearchPage/utilities/engagementSignal';
import {
  highlightNumberInText,
  shouldShowRatingsReviews,
  shouldShowRepeatCount,
  shouldShowYearsOfExperience,
} from '@rover/react-lib/src/pages/search/SearchPage/utilities/searchCardTreatment';
import type { CommonSearchResult } from '@rover/types/src/CommonSearchResult';
import { PetType } from '@rover/types/src/Pet';

import SimplifiedRatingsReview from '../../RatingsReview/SimplifiedRatingsReview';
import SitterHighlight from '../../SitterHighlight/SitterHighlight';

import ExpandableReviewSnippet from './ExpandableReviewSnippet';

export type Props = {
  searchResult: CommonSearchResult;
  preferCatReviews: boolean;
  showReviewSnippet: boolean;
};

const SitterHighlightsSection = ({
  searchResult,
  preferCatReviews,
  showReviewSnippet,
}: Props): React.ReactElement => {
  const { t, i18n } = useLingui();
  const {
    reviewsCount,
    testimonialsCount,
    repeatClientCount,
    yearsOfExperience,
    ratingsAverage,
    engagementSignal,
    reviewText,
    catReviewText,
    relevantReviewHighlights,
  } = searchResult;

  const showRatingsReviews = shouldShowRatingsReviews(reviewsCount, testimonialsCount);
  const showRepeatCount = shouldShowRepeatCount(repeatClientCount);
  const showYearsOfExperience = shouldShowYearsOfExperience(
    yearsOfExperience,
    showRatingsReviews,
    showRepeatCount
  );
  const engagementSignalContent = getEngagementSignalContent(i18n, engagementSignal);
  const reviewPetType = preferCatReviews && catReviewText ? PetType.CAT : PetType.DOG;
  const reviewTextToUse = reviewPetType === PetType.CAT ? catReviewText : reviewText;

  return (
    <Flex rxnProps={{ accessible: true }} flexDirection="column" gap="2x">
      {showRatingsReviews && (
        <SimplifiedRatingsReview
          ratingsAverage={ratingsAverage}
          reviewsCount={reviewsCount}
          testimonialsCount={testimonialsCount}
        />
      )}
      {showRepeatCount && (
        <SitterHighlight
          dataTestId="repeat-clients-highlight"
          text={highlightNumberInText(
            t({
              message: plural(repeatClientCount, {
                one: '# repeat client',
                other: '# repeat clients',
              }),
            })
          )}
          icon={Rebook}
        />
      )}
      {showYearsOfExperience && (
        <SitterHighlight
          dataTestId="years-of-experience-highlight"
          text={highlightNumberInText(yearsOfExperience)}
          icon={Trophy}
        />
      )}
      {engagementSignalContent && (
        <SitterHighlight
          dataTestId="engagement-signal-highlight"
          text={engagementSignalContent.fullText}
          icon={engagementSignalContent.icon}
          highlightColor={engagementSignalContent.color}
        />
      )}
      {showReviewSnippet && reviewTextToUse && (
        <ExpandableReviewSnippet
          reviewText={reviewTextToUse}
          reviewPetType={reviewPetType}
          // The matched passages belong to the dog review; the cat review is a
          // different review with no highlights of its own.
          reviewHighlights={reviewPetType === PetType.CAT ? null : relevantReviewHighlights}
        />
      )}
    </Flex>
  );
};

export default SitterHighlightsSection;
